import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  ApiError,
  catalog as catalogApi,
  RANK_LABELS,
  RANK_ORDER,
  type CatalogEntry,
  type CatalogResponse,
} from '../api';
import { Attribution } from '../components/Attribution';
import { useUnits } from '../units';

const CATEGORY_LABELS: Record<string, string> = {
  required: 'Required',
  elective: 'Elective',
  special_elective: 'Shooting sports',
};

export function Catalog() {
  const { unitId = '' } = useParams();
  const { currentUnit, hasRole } = useUnits();
  const mayView = hasRole('ADMIN') || hasRole('DEN_LEADER');
  const isAdmin = hasRole('ADMIN');

  const [data, setData] = useState<CatalogResponse | null>(null);
  const [rank, setRank] = useState('');
  const [category, setCategory] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    setError(null);
    catalogApi
      .get(unitId, {
        rank: rank || undefined,
        category: category || undefined,
        includeRetired: showRetired || undefined,
      })
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load the catalog'));
  }, [unitId, rank, category, showRetired]);

  useEffect(() => {
    if (mayView) load();
  }, [mayView, load]);

  // SCO-72 — section by category, Required first, then Elective, then Shooting sports.
  // Within a section, order by rank then name.
  const grouped = useMemo(() => {
    const order = ['required', 'elective', 'special_elective'];
    const rankIdx = (r: string) => {
      const i = RANK_ORDER.indexOf(r);
      return i === -1 ? RANK_ORDER.length : i;
    };
    const by = new Map<string, CatalogEntry[]>();
    for (const a of data?.adventures ?? []) {
      const key = order.includes(a.category) ? a.category : 'other';
      const list = by.get(key);
      if (list) list.push(a);
      else by.set(key, [a]);
    }
    for (const list of by.values()) {
      list.sort((x, y) => rankIdx(x.rank) - rankIdx(y.rank) || x.name.localeCompare(y.name));
    }
    return [...order, 'other']
      .filter((c) => by.has(c))
      .map((c) => [c, by.get(c) as CatalogEntry[]] as const);
  }, [data]);

  if (!mayView) return <Navigate to="/" replace />;

  async function runSync() {
    setSyncing(true);
    setError(null);
    try {
      await catalogApi.sync(unitId);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  const sync = data?.sync;

  return (
    <div className="panel">
      <div className="row">
        <div>
          <h1>Adventure catalog</h1>
          <p className="muted">{currentUnit?.name}</p>
        </div>
        {isAdmin && (
          <button onClick={runSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Refresh from open-scout-api'}
          </button>
        )}
      </div>

      {sync && (sync.stale || sync.lastError) && (
        <p className={sync.lastError ? 'error' : 'muted'}>
          {sync.lastError
            ? `Last sync had a problem: ${sync.lastError}. Showing the last good catalog.`
            : 'Catalog data is stale — a refresh is overdue.'}
        </p>
      )}
      {error && <p className="error">{error}</p>}

      <div className="row" style={{ gap: 12, alignItems: 'center', margin: '12px 0' }}>
        <label className="filter">
          Rank{' '}
          <select value={rank} onChange={(e) => setRank(e.target.value)}>
            <option value="">All</option>
            {RANK_ORDER.map((r) => (
              <option key={r} value={r}>
                {RANK_LABELS[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="filter">
          Type{' '}
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All</option>
            <option value="required">Required</option>
            <option value="elective">Elective</option>
            <option value="special_elective">Shooting sports</option>
          </select>
        </label>
        <label className="filter">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
          />{' '}
          Show retired
        </label>
        <span className="muted small">
          {data?.adventures.length ?? '…'} shown ·{' '}
          {sync?.lastOkAt ? `synced ${new Date(sync.lastOkAt).toLocaleDateString()}` : 'never synced'}
          {sync?.sourceTag ? ` (${sync.sourceTag})` : ''}
        </span>
      </div>

      {grouped.map(([cat, entries]) => (
        <section key={cat}>
          <h2>{CATEGORY_LABELS[cat] ?? 'Other'}</h2>
          <table className="grid">
            <thead>
              <tr>
                <th>Adventure</th>
                <th>Rank</th>
                <th>Requirements</th>
                <th>Supplies</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((a) => (
                <AdventureRow key={a.sourceId} unitId={unitId} entry={a} isAdmin={isAdmin} onChanged={load} />
              ))}
            </tbody>
          </table>
        </section>
      ))}

      {data && <Attribution />}
    </div>
  );
}

/** SCO-35 — serialize/parse the per-unit requirement override as "number | text" lines. */
function reqsToText(reqs: { number: string; text: string }[] | null): string {
  return (reqs ?? []).map((r) => `${r.number} | ${r.text}`).join('\n');
}
function textToReqs(text: string): { number: string; text: string }[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      const sep = line.indexOf('|');
      if (sep === -1) return { number: String(i + 1), text: line };
      return { number: line.slice(0, sep).trim() || String(i + 1), text: line.slice(sep + 1).trim() };
    })
    .filter((r) => r.text.length > 0);
}

function AdventureRow({
  unitId,
  entry,
  isAdmin,
  onChanged,
}: {
  unitId: string;
  entry: CatalogEntry;
  isAdmin: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(entry.name);
  const [note, setNote] = useState(entry.note ?? '');
  const [supplyText, setSupplyText] = useState((entry.supplyList ?? []).join('\n'));
  const [reqText, setReqText] = useState(reqsToText(entry.requirements));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    name.trim() !== entry.name ||
    note.trim() !== (entry.note ?? '') ||
    supplyText !== (entry.supplyList ?? []).join('\n') ||
    (!entry.isShootingSports && reqText !== reqsToText(entry.requirements));

  async function saveCustomizations() {
    setBusy(true);
    setError(null);
    try {
      const patch: Parameters<typeof catalogApi.override>[2] = {};
      if (name.trim() !== entry.name) patch.nameOverride = name.trim() || null;
      if (note.trim() !== (entry.note ?? '')) patch.note = note.trim() || null;
      if (supplyText !== (entry.supplyList ?? []).join('\n')) {
        const list = supplyText.split('\n').map((s) => s.trim()).filter(Boolean);
        patch.supplyList = list.length ? list : null;
      }
      if (!entry.isShootingSports && reqText !== reqsToText(entry.requirements)) {
        const parsed = textToReqs(reqText);
        patch.requirementsOverride = parsed.length ? parsed : null;
      }
      await catalogApi.override(unitId, entry.sourceId, patch);
      onChanged();
    } catch {
      setError('Could not save');
    } finally {
      setBusy(false);
    }
  }

  async function toggleHidden() {
    setBusy(true);
    try {
      await catalogApi.override(unitId, entry.sourceId, { hidden: !entry.hidden });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function resetToDefault() {
    if (!confirm(`Discard this pack's customizations of "${entry.name}" and use the open-scout-api version?`)) return;
    setBusy(true);
    try {
      await catalogApi.clearOverride(unitId, entry.sourceId);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td>
          <button className="linklike" onClick={() => setOpen((o) => !o)}>
            {entry.name}
          </button>
          {entry.customized && <span className="badge" style={{ marginLeft: 6 }}>customized</span>}
          {entry.hidden && <span className="badge" style={{ marginLeft: 6 }}>hidden</span>}
          {!entry.active && (
            <span className="badge" style={{ marginLeft: 6 }} title={entry.retiredAt ?? undefined}>
              retired{entry.supersededBy ? ` → ${entry.supersededBy}` : ''}
            </span>
          )}
        </td>
        <td>{RANK_LABELS[entry.rank] ?? entry.rank}</td>
        <td>
          {entry.hasNoPublishedRequirements
            ? '— none published —'
            : entry.requirements
              ? `${entry.requirements.length}`
              : '—'}
        </td>
        <td>{entry.supplyList?.length ?? '—'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={4} className="adv-detail">
            {entry.officialUrl && (
              <p>
                <a href={entry.officialUrl} target="_blank" rel="noreferrer">
                  Official page ↗
                </a>
              </p>
            )}
            {entry.isShootingSports ? (
              <p className="muted">
                Completed only at approved events with qualified instructors — no requirement
                checklist.
              </p>
            ) : (
              entry.hasNoPublishedRequirements &&
              !isAdmin && (
                <p className="muted">
                  No requirements published in the catalog yet — an Admin can add them, or a catalog
                  sync may fill them in.
                </p>
              )
            )}
            {entry.requirements && !isAdmin && (
              <ol className="reqs">
                {entry.requirements.map((r) => (
                  <li key={r.number}>{r.text}</li>
                ))}
              </ol>
            )}
            {entry.requirements && <Attribution textRights={entry.textRights} />}

            {isAdmin && (
              <div className="supply-editor">
                <label>
                  Display name
                  <input value={name} onChange={(e) => setName(e.target.value)} />
                </label>
                {!entry.isShootingSports && (
                  <label>
                    Requirements (one per line, <code>number | text</code>)
                    <textarea
                      rows={Math.min(10, Math.max(3, reqText.split('\n').length))}
                      value={reqText}
                      onChange={(e) => setReqText(e.target.value)}
                    />
                  </label>
                )}
                <label>
                  Supply list (one per line)
                  <textarea rows={3} value={supplyText} onChange={(e) => setSupplyText(e.target.value)} />
                </label>
                <label>
                  Note for leaders (optional)
                  <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                </label>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <button className="primary" onClick={saveCustomizations} disabled={busy || !dirty}>
                    Save customizations
                  </button>
                  <button onClick={toggleHidden} disabled={busy}>
                    {entry.hidden ? 'Un-hide' : 'Hide from planning'}
                  </button>
                  {entry.customized && (
                    <button onClick={resetToDefault} disabled={busy}>
                      Reset to open-scout-api
                    </button>
                  )}
                  {error && <span className="error">{error}</span>}
                </div>
                <p className="hint">
                  Customizations are per-pack and are kept across catalog syncs.
                </p>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
